import { useState } from 'react';
import { Alert, Box, Button, Center, Divider, Paper, Stack, Text, TextInput, PasswordInput } from '@mantine/core';
import { Navigate, useLocation } from 'react-router-dom';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { useAuthStore } from './authStore';
import { tokens as T } from '../theme/tokens';

/* Sign-in.
 *
 * Email and password only, because that is the provider enabled on this project
 * and because an operator console with a self-service path is a contradiction:
 * accounts are created by `haus user create` and granted by
 * `haus user claims set`. */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const phase = useAuthStore((s) => s.phase);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const location = useLocation() as { state?: { from?: { pathname?: string } } };

  async function submit() {
    const auth = getFirebaseAuth();
    if (!auth) {
      setError('This build carries no Firebase configuration.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** Google sign-in. The provider is already enabled on the project, so this is
   *  purely a client affordance — and the gate is unchanged either way: an
   *  account without the superAdmin claim signs in and is then refused by
   *  ProtectedRoute, which is the same fail-closed path as a wrong password. */
  async function google() {
    const auth = getFirebaseAuth();
    if (!auth) {
      setError('This build carries no Firebase configuration.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      // Always show the account chooser. Without it Google silently re-selects
      // the last account, so an operator who just signed out because they used
      // the WRONG account lands straight back on it with no way to say
      // otherwise — which is the most likely reason anyone is on this page
      // twice.
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Signing in is not the same as arriving. Firebase updates its own state and
  // nothing else moves, so without this the operator sits on a login form that
  // has already succeeded. `from` is what ProtectedRoute saved on the way out.
  //
  // It leaves on ANY signed-in session, not only an operator's. Gating this on
  // the claim stranded a non-operator on a login form that had worked
  // perfectly and said nothing — no error, no identity, no way forward, and
  // signing in again just repeated it. Leaving hands them to ProtectedRoute,
  // which names the account and offers a way out.
  if (phase === 'signedIn') {
    return <Navigate to={isSuperAdmin ? (location.state?.from?.pathname ?? '/') : '/'} replace />;
  }

  return (
    <Center h="100vh" style={{ background: T.page }}>
      <Paper radius="xl" p={32} w={400}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
          <Box style={{ width: 26, height: 26, borderRadius: 999, background: T.coral }} />
          <Box style={{ lineHeight: 1.2 }}>
            <Text fz={14} fw={800}>
              Bit Haus
            </Text>
            <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }}>
              CONTROL PLANE
            </Text>
          </Box>
        </Box>
        <Text fz={20} fw={800} mb={6}>
          Operator sign-in
        </Text>
        <Text fz={13} c={T.ink4} mb={20} lh={1.55}>
          Restricted to operators carrying the superAdmin claim. Accounts are provisioned with{' '}
          <Text component="span" ff="monospace" fz={12}>
            haus user
          </Text>
          .
        </Text>
        {phase === 'unconfigured' ? (
          <Alert color="orange" radius="lg" mb={16} title="Not configured">
            This build has no Firebase settings, so sign-in is unavailable.
          </Alert>
        ) : null}
        {error ? (
          <Alert color="red" radius="lg" mb={16}>
            {error}
          </Alert>
        ) : null}
        <Stack gap={12}>
          <TextInput
            label="Email"
            value={email}
            autoComplete="username"
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <PasswordInput
            label="Password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
          <Button mt={8} loading={busy} onClick={() => void submit()}>
            Sign in
          </Button>
          <Divider label="or" labelPosition="center" my={2} />
          <Button variant="default" loading={busy} onClick={() => void google()}>
            Continue with Google
          </Button>
        </Stack>
      </Paper>
    </Center>
  );
}
